// src/services/callThreads.js — the tail of what was said DURING a call.
//
// A group chat's call-status row previews the call's own chat: the last thing
// anybody said in it, and whose face said it. That thread lives at
// chats/{chatId}/call_threads/{sessionId}/messages and is deliberately NOT part
// of the conversation — which is exactly why the row has to surface it, because
// nothing else ever will.
//
// 🟥 ONE READ PER CALL, NOT ONE PER RENDER. A long-running group chat holds
//    dozens of finished calls, and React re-renders the message list on every
//    keystroke in the composer. A fetch inside the row component would therefore
//    re-read Firestore continuously for rows that have not changed since the call
//    ended. Results are memoised per session id and shared by every subscriber,
//    so N rows rendering M times cost N reads.
//
// 🟢 THE MEMO EXPIRES, because the thread is only ALMOST immutable. The host
//    writes the status row on its way out of the call while other participants
//    may still be inside it and still typing. Caching forever would freeze a
//    preview that was taken one second too early.
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

const TTL_MS = 60000;
/* enough to say "12 messages" honestly and to hold the newest one; a call that
   produced more than this is previewed as "20+", never as a wrong total */
const TAIL = 20;

const cache = new Map();   // key -> { at, value, promise? }
const subs = new Set();

const keyOf = (chatId, sessionId) => `${chatId}::${sessionId}`;
const EMPTY = { count: 0, last: null, capped: false };

function notify() {
  for (const f of subs) {
    try { f(); } catch { /* one bad subscriber must not stop the others */ }
  }
}

export function subscribeCallThreads(fn) { subs.add(fn); return () => subs.delete(fn); }

/** What is already known, or null. Never starts a read — safe to call in render. */
export function peekCallThread(chatId, sessionId) {
  if (!chatId || !sessionId) return null;
  const e = cache.get(keyOf(chatId, sessionId));
  return (e && e.value) || null;
}

/** Read the tail once, de-duped across callers and memoised for TTL_MS. */
export function loadCallThread(chatId, sessionId) {
  if (!chatId || !sessionId) return Promise.resolve(null);
  const k = keyOf(chatId, sessionId);
  const e = cache.get(k);
  /* a read already in flight, or a fresh answer — either way, don't ask again */
  if (e && Date.now() - e.at < TTL_MS) return e.promise || Promise.resolve(e.value);

  const promise = (async () => {
    let value = EMPTY;
    try {
      const snap = await getDocs(query(
        collection(db, 'chats', chatId, 'call_threads', sessionId, 'messages'),
        orderBy('timestamp', 'desc'), limit(TAIL),
      ));
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      value = { count: docs.length, last: docs[0] || null, capped: docs.length === TAIL };
    } catch {
      /* rules, offline, or a call nobody typed in — all three mean "nothing to
         preview", and none of them should make a message row render an error */
      value = EMPTY;
    }
    cache.set(k, { at: Date.now(), value });
    notify();
    return value;
  })();

  /* keep any previous answer visible while the refresh is in flight, so a row
     that already had a preview does not blink back to nothing */
  cache.set(k, { at: Date.now(), promise, value: (e && e.value) || null });
  return promise;
}

/** Drop what is remembered about one call — used after sending into its thread. */
export function forgetCallThread(chatId, sessionId) {
  if (!chatId || !sessionId) return;
  cache.delete(keyOf(chatId, sessionId));
  notify();
}
